import React from "react";
import color from "color";
import brands from "src/components/theme/brands.json";

// A generic size class used for things like breakpoints
// and component width/height variant props.
export type Size = "xs" | "sm" | "md" | "lg" | "xl";

interface VisualTheme {
  axis_legend_color: string;
  axis_text_color: string;
  axis_zero_color: string;
  background_color: string;
  chart_colors?: string[];
  chart_title_color: string;
  created_by_color: string;
  description_color: string;
  grid_lines_color: string;
  series_legend_color: string;
  username_color: string;
}

// Create theme color CSS vars from a single base color.
export const createThemeColors = (hex: string) => {
  const c = color(hex);
  const w = color("#fff");
  const b = color("#000");

  const hslw = w.hsl().string();
  const hslb = b.hsl().string();
  const text = c.contrast(w) > 4 ? hslw : hslb;

  return {
    "--color1-100": hslw,
    "--color1-200": c.alpha(0.1).hsl().string(),
    "--color1-300": c.alpha(0.3).hsl().string(),
    "--color1-400": c.alpha(0.5).hsl().string(),
    "--color1-500": c.hsl().string(),
    "--color1-100-stroke": hslb,
    "--color1-200-stroke": hslb,
    "--color1-300-stroke": hslb,
    "--color1-400-stroke": hslb,
    "--color1-500-stroke": text,
    color: hslb,
  } as React.CSSProperties;
};

const defaultTheme: VisualTheme = brands["dune"];

// Find theme for a given brand name.
// Some brands have a randomized key to make discovery slightly less obvious.
export const getTheme = (brand: string) => {
  if (brand in brands) {
    return (brands as { [key: string]: VisualTheme })[brand];
  }
  return defaultTheme;
};

const ThemeContext: React.Context<VisualTheme> =
  React.createContext(defaultTheme);

export const ThemeProvider: React.FC<{
  theme: VisualTheme;
  children: React.ReactNode;
}> = (props) => {
  return (
    <ThemeContext.Provider value={props.theme}>
      {props.children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): VisualTheme => {
  return React.useContext(ThemeContext);
};

// Extra colors for charts, ordered by preference.
// https://carto.com/carto-colors
export const chartColors = [
  "#56569b",
  "#f4603e",
  "#909090",

  "#cc61b0",
  "#f2b701",
  "#5d69b1",
  "#52bca3",
  "#99c945",
  "#24796c",
  "#e68310",
  "#2f8ac4",
  "#764e9f",
  "#ed645a",
  "#cc3a8e",
  "#a5aa99",

  "#66c5cc",
  "#f6cf71",
  "#f89c74",
  "#dcb0f2",
  "#87c55f",
  "#9eb9f3",
  "#fe88b1",
  "#c9db74",
  "#8be0a4",
  "#b497e7",
  "#d3b484",
  "#b3b3b3",

  "#7f3c8d",
  "#11a579",
  "#3969ac",
  "#e73f74",
  "#80ba5a",
  "#008695",
  "#cf1c90",
  "#f97b72",
  "#4b4b8f",
  "#e58606",
  "#5f4690",
  "#1d6996",

  "#38a6a5",
  "#0f8554",
  "#73af48",
  "#edad08",
  "#e17c05",
  "#cc503e",
  "#94346e",
  "#6f4070",
  "#994e95",
  "#666666",
  "#009392",
  "#39b185",

  "#9ccb86",
  "#e9e29c",
  "#eeb479",
  "#e88471",
  "#cf597e",
  "#88CCEE",
  "#CC6677",
  "#DDCC77",
  "#117733",
  "#332288",
  "#AA4499",
  "#44AA99",

  "#999933",
  "#882255",
  "#661100",
  "#6699CC",
  "#888888",
  "#fcde9c",
  "#faa476",
  "#f0746e",
  "#e34f6f",
  "#dc3977",
  "#b9257a",
  "#7c1d6f",

  "#fbe6c5",
  "#f5ba98",
  "#ee8a82",
  "#dc7176",
  "#c8586c",
  "#9c3f5d",
  "#70284a",

  // ChatGPT colors
  "#5a8a89",
  "#f49a4a",
  "#6f6f6f",
  "#ba89c0",
  "#ffb400",
  "#4278a7",
  "#3cb393",
  "#a1d85d",
  "#2c7d6e",
  "#e7701c",
  "#479bd3",
  "#8056ab",

  "#f2675c",
  "#ba4d92",
  "#95a28e",
  "#7dd3d9",
  "#f9d77d",
  "#fb9d7f",
  "#e3c2f6",
  "#98c978",
  "#a7bdef",
  "#ff7db9",
  "#d5e074",
  "#a7e3b2",

  "#bf9be8",
  "#d8c89c",
  "#c2c2c2",
  "#8b3f89",
  "#14aa86",
  "#4d73b1",
  "#ea567a",
  "#7eba69",
  "#006a80",
  "#d442a2",
  "#fc8a82",
  "#695aa6",
];
